/**
 * Client for the atlas backend Spatial Analysis run API
 * (`/api/spatial-analysis/...`). The MVP transport is polling: create a run,
 * then poll `getRun` for the full, progressively-updated state.
 */

import axios from 'axios';
import { atlasBackendApi } from '../api/atlasBackendClient';

import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { CreateSpatialAnalysisRunRequest, SpatialAnalysisRun } from './types';

const RUNS_API_BASE = `${getAtlasApiBaseUrl()}/api/spatial-analysis/runs`;

/** Read the atlas auth token from localStorage (matches mapService). */
function getAuthToken(): string {
  const tokenString = localStorage.getItem('atlas_authToken');
  if (!tokenString) {
    throw new Error('No authentication token found. Please log in first.');
  }
  try {
    const parsed = JSON.parse(tokenString);
    if (parsed?.access_token) {
      return parsed.access_token;
    }
  } catch {
    // Not JSON — use the raw string.
  }
  return tokenString;
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getAuthToken()}` };
}

export async function createSpatialAnalysisRun(
  request: CreateSpatialAnalysisRunRequest,
): Promise<SpatialAnalysisRun> {
  const response = await atlasBackendApi.post<SpatialAnalysisRun>(RUNS_API_BASE, request, {
    headers: authHeaders(),
    timeout: 15000,
  });
  return response.data;
}

export async function getSpatialAnalysisRun(runId: string): Promise<SpatialAnalysisRun> {
  const response = await atlasBackendApi.get<SpatialAnalysisRun>(`${RUNS_API_BASE}/${runId}`, {
    headers: authHeaders(),
    timeout: 15000,
  });
  return response.data;
}

export async function cancelSpatialAnalysisRun(runId: string): Promise<SpatialAnalysisRun> {
  const response = await atlasBackendApi.post<SpatialAnalysisRun>(
    `${RUNS_API_BASE}/${runId}/cancel`,
    {},
    { headers: authHeaders(), timeout: 15000 },
  );
  return response.data;
}

/**
 * Polygon-clipped GTFS feed (GTFSFeedData rows) for a run, for importing into the
 * Fixed Routes drawer. Returns null when the run had no usable GTFS feed (404).
 */
export async function getSpatialAnalysisGtfsFeed(runId: string): Promise<Record<string, unknown> | null> {
  try {
    const response = await atlasBackendApi.get(`${RUNS_API_BASE}/${runId}/gtfs`, {
      headers: authHeaders(),
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function publishSpatialAnalysisRun(
  runId: string,
): Promise<{ available: boolean; message?: string }> {
  const response = await atlasBackendApi.post(
    `${RUNS_API_BASE}/${runId}/publish`,
    {},
    { headers: authHeaders(), timeout: 15000 },
  );
  return response.data;
}
